import { ReactNode, useMemo } from 'react';
import { ThemeProps, useCurrentTheme } from '../themes';
import { DataSortVO } from '@mc/data-filter-react';
import {
  DataAggregateVO,
  DataGroupVO,
  FinalTableAlignmentType,
  FinalTableColumn,
  FinalTableColumnType,
  FinalTableComputedColumn,
  FinalTableConfig,
  FinalTableProps,
  TblAggregateType,
} from './FinalTableProps';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { IData } from '../virtual-data/VirtualData';
import { AreaKey, ColumnInfo, getAreaKey } from '../rows-data/types';

import { TextCell } from '../table/TextCell';
import { IntegerCell } from '../table/IntegerCell';
import { DecimalCell } from '../table/DecimalCell';
import { DateCell } from '../table/DateCell';
import { DateTimeCell } from '../table/DateTimeCell';
import { CurrencyCell } from '../table/CurrencyCell';
import { CurrencyDecimalCell } from '../table/CurrencyDecimalCell';
import { BooleanCell } from '../table/BooleanCell';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
} from '@mc/react-format';
import { getDataRowColumnValue } from '../data-table/dataUtils';

export const GROUP_COLUMN_ID = '__group__';
export const DEFAULT_COLUMN_WIDTH = 200;
export const MIN_COL_WIDTH = 100;

export type TableRowsHeights = {
  rowHeight: number;
  headerHeight: number;
  groupRowHeight: number;
  footerHeight: number;
  groupDepth0Spacing: number;
};

export const getTableAligmentByType = (
  type?: FinalTableColumnType
): string | undefined => {
  if (!type) {
    return '';
  }

  switch (type) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
      return Classes.TABLE_ALIGN_RIGHT;
    default:
      return undefined;
  }
};

export const getFinalTableColumnCellValue = <DATA extends IData<ID>, ID>(
  column: FinalTableComputedColumn<DATA>,
  row: any
) => {
  const par = column.accessor || column.id;
  if (!par) {
    return undefined;
  }

  return getDataRowColumnValue(row, par);
};

export const getTableAligment = (
  aligment: FinalTableAlignmentType
): string | undefined => {
  switch (aligment) {
    case 'center':
      return Classes.TABLE_ALIGN_CENTER;
    case 'right':
      return Classes.TABLE_ALIGN_RIGHT;
    case 'left':
      return Classes.TABLE_ALIGN_LEFT;
    default:
      return undefined;
  }
};

export const getCell = (type?: FinalTableColumnType) => {
  switch (type) {
    case 'text':
      return TextCell;
    case 'integer':
      return IntegerCell;
    case 'decimal':
      return DecimalCell;
    case 'date':
      return DateCell;
    case 'dateTime':
      return DateTimeCell;
    case 'currency':
      return CurrencyCell;
    case 'currencyDecimal':
      return CurrencyDecimalCell;
    case 'boolean':
      return BooleanCell;
    default:
      return undefined;
  }
};

export const useHeights = (
  rowHeight?: number,
  headerHeight?: number,
  groupRowHeight?: number,
  footerHeight?: number
): TableRowsHeights => {
  const theme = useCurrentTheme();

  return useMemo(() => {
    let resultRowHeight =
      rowHeight !== undefined ? rowHeight : theme.dataTableRowHeight;

    return {
      headerHeight:
        headerHeight !== undefined ? headerHeight : theme.dataTableHeaderHeight,
      rowHeight: resultRowHeight,
      groupRowHeight:
        groupRowHeight !== undefined ? groupRowHeight : resultRowHeight,
      footerHeight: footerHeight !== undefined ? footerHeight : resultRowHeight,
      groupDepth0Spacing: 3,
    };
  }, [theme, headerHeight, rowHeight, groupRowHeight]);
};

type ComputeSort = { groupSort: DataSortVO[]; colSort: DataSortVO[] };

export const computeSort = (
  sort: DataSortVO[],
  grouping: DataGroupVO[]
): ComputeSort => {
  if (grouping.length === 0) {
    return { groupSort: [], colSort: sort };
  } else {
    const result: ComputeSort = {
      groupSort: [],
      colSort: [],
    };

    const groupingMap: { [key: string]: boolean } = {};
    grouping.forEach((g) => {
      groupingMap[g.field] = true;
    });

    const sortMap: { [key: string]: DataSortVO } = {};
    sort.forEach((s) => {
      sortMap[s.field] = s;
    });

    // Jako první řazení dle skupin
    grouping.forEach((g) => {
      result.groupSort.push(sortMap[g.field] || { field: g.field, asc: true });
    });

    // Řazení dle ostatních sloupců
    sort.forEach((s) => {
      if (!groupingMap[s.field]) {
        result.colSort.push(s);
      }
    });

    return result;
  }
};

/**
 * Vrácení seznamu viditelných sloupců - součástí viditelných jsou i sloupce dle kterých je seskupeno.
 * @param columnsView
 */
export const getVisibleColumns = <DATA extends IData<ID>, ID>(
  columnsView: ColumnsView<DATA, ID>
): string[] => {
  let visibleColumns: string[] = [];
  columnsView.groupedColumns.forEach((c) => {
    if (c.type !== 'action') {
      visibleColumns.push(getColumnField(c));
    }
  });
  columnsView.otherColumns.forEach((c) => {
    if (c.type !== 'action') {
      visibleColumns.push(getColumnField(c));
    }
  });
  return visibleColumns;
};

/**
 * Struktura pro uchování informmací pro aktuální zobrazení a seskupení sloupců, jejich pořadí apod., nejsou zde neviditelné sloupce.
 */
export type ColumnsView<DATA extends IData<ID>, ID> = {
  /**
   * Sloupce dle kterých je seskupeno.
   */
  groupedColumns: FinalTableColumn<DATA>[];
  /**
   * Ostatní sloupce.
   */
  otherColumns: FinalTableColumn<DATA>[];
  meta: {
    sortedColumns: FinalTableColumn<DATA>[];
    visibleColumns: FinalTableColumn<DATA>[];
  };
};

export const getColumnsView = <DATA extends IData<ID>, FILTER, ID>(
  tableGrouping: DataGroupVO[],
  tableColumns: ColumnInfo[],
  tableConfig: FinalTableConfig<DATA, FILTER, ID>
): ColumnsView<DATA, ID> => {
  // Sloupce, dle kterých se seskupuje, dáme dohromady pod jeden sloupec - zobrazují se v hlavičcce v jedné buňce
  const groupedColumns: FinalTableColumn<DATA>[] = [];
  const otherColumns: FinalTableColumn<DATA>[] = [];
  const groupingNames = tableGrouping.map((x) => x.field);

  // Seřazení sloupců dle columnsDefinice
  // Mapa identifkátoru na index v seznamu tableColumns
  const columnsInfoMap: { [key: string]: number } = {};
  tableColumns.forEach((c, index) => {
    columnsInfoMap[c.ident] = index;
  });

  const sortedColumns = [...tableConfig.columns].sort((a, b) => {
    const aci = columnsInfoMap[a.id || a.accessor || ''];
    const bci = columnsInfoMap[b.id || b.accessor || ''];

    let result;
    if (aci === undefined && bci === undefined) {
      result = tableConfig.columns.indexOf(a) - tableConfig.columns.indexOf(b);
    } else if (aci === undefined && bci !== undefined) {
      result = 1;
    } else if (aci !== undefined && bci === undefined) {
      result = -1;
    } else {
      result = aci - bci;
    }

    return result;
  });

  let sortedVisibleColumns;
  if (tableColumns.length === 0) {
    sortedVisibleColumns = sortedColumns;
  } else {
    sortedVisibleColumns = sortedColumns.filter((x) => {
      const info = columnsInfoMap[x.id || x.accessor || ''];
      if (
        x.type === 'action' ||
        groupingNames.some((i) => i === (x.id || x.accessor || ''))
      ) {
        return true;
      }
      return info !== undefined ? tableColumns[info].visible : false;
    });
  }
  // console.log(
  //   'sortedVisibleColumns',
  //   sortedColumns,
  //   sortedVisibleColumns,
  //   columnsInfoMap
  // );
  // Rozdělení sloupců na grouped a ostatní
  sortedVisibleColumns.forEach((column) => {
    if (column.groupable && !column.accessor) {
      console.error(
        'Sloupec má povolené seskupování (groupable), ale nemá definován accessor.',
        column
      );
      throw new Error(
        'Sloupec má povolené seskupování (groupable), ale nemá definován accessor.'
      );
    }

    const field = getColumnFieldNoCheck(column);
    if (field && groupingNames.indexOf(field) >= 0) {
      groupedColumns.push(column);
    } else {
      otherColumns.push(column);
    }
  });

  // Seřazení grouped column dle grouping pořadí
  groupedColumns.sort((a, b) => {
    let afield = getColumnFieldNoCheck(a);
    let bfield = getColumnFieldNoCheck(b);
    const grouping = tableGrouping.map((x) => x.field);

    if (afield && bfield) {
      let an = grouping.indexOf(afield);
      let bn = grouping.indexOf(bfield);
      if (an < bn) {
        return -1;
      } else if (an > bn) {
        return 1;
      }
    }

    return 0;
  });

  return {
    groupedColumns,
    otherColumns,
    meta: {
      sortedColumns: sortedColumns,
      visibleColumns: sortedVisibleColumns,
    },
  };
};

export const getFormattedColumns = <DATA extends IData<ID>, FILTER, ID>(
  finalTableProps: FinalTableProps<DATA, FILTER, ID>
) => {
  let columnsView = getColumnsView(
    finalTableProps.grouping,
    finalTableProps.columns,
    finalTableProps.config
  );

  const result: FinalTableComputedColumn<DATA>[] = [];

  // První sloupec - seskupovací sloupec - pokud je
  if (columnsView.groupedColumns.length > 0) {
    const formattedColumn: FinalTableComputedColumn<DATA> = {
      type: '_group',
      id: GROUP_COLUMN_ID,
      className: classNames(getTableAligment('left')),
      // @ts-ignore
      groupColumns: columnsView.groupedColumns.map((x) =>
        computeColumn(
          x,
          undefined,
          finalTableProps.config.sortable,
          finalTableProps.config.groupable
        )
      ),
      group: true,
      align: 'left',
      sortable: false,
      groupable: false,
    };

    result.push(formattedColumn);
  }

  // Ostatní sloupce
  columnsView.otherColumns.forEach((column) => {
    result.push(
      computeColumn(
        column,
        finalTableProps.getAggregate(getColumnFieldNoCheck(column)),
        finalTableProps.config.sortable,
        finalTableProps.config.groupable
      )
    );
  });

  return result;
};

const computeColumn = <DATA extends object = {}>(
  column: FinalTableColumn<DATA>,
  columnAggregate?: DataAggregateVO,
  defaultSortable?: boolean,
  defaultGroupable?: boolean
) => {
  const alignClass = classNames(
    column.align
      ? getTableAligment(column.align)
      : getTableAligmentByType(column?.type) || Classes.TABLE_ALIGN_LEFT,
    column.className
  );
  const { className, cell, ...rest } = column;

  const aggregatedType = convertColumnTypeToAggregatedType(
    column.type,
    column.aggregatedType,
    columnAggregate?.aggregate
  );

  // @ts-ignore
  const formattedColumn: FinalTableComputedColumn<D> = {
    ...rest,
    check: false,
    group: false,
    // @ts-ignore
    className: alignClass,
    // @ts-ignore
    ...((column.cell || column.type) && {
      // @ts-ignore
      cell: column.cell ? column.cell : getCell(column.type),
    }),
    // @ts-ignore
    ...((column.aggregatedCell || (column.aggregatedType ?? column.type)) && {
      // @ts-ignore
      aggregatedCell: column.aggregatedCell
        ? column.aggregatedCell
        : getCell(aggregatedType),
    }),
    aggregatedType,
  };

  if (
    column.aggregatedNumberProps?.fractionDigits === undefined &&
    (column.aggregatedType === 'decimal' ||
      column.aggregatedType === 'currencyDecimal')
  ) {
    formattedColumn.aggregatedNumberProps = {
      ...column.aggregatedNumberProps,
      fractionDigits: 2,
    };
  }

  // Sortable + groupable
  if (column.type !== 'action') {
    // Sortable
    if (column.sortable === true) {
      formattedColumn.sortable = true;
    } else if (column.sortable === false) {
      formattedColumn.sortable = false;
    } else {
      if (defaultSortable === undefined || defaultSortable === true) {
        // Sortable dle typu sloupce
        formattedColumn.sortable = supportsSorting(column.type);
      } else {
        formattedColumn.sortable = false;
      }
    }

    // Groupable
    if (column.groupable === true) {
      formattedColumn.groupable = true;
    } else if (column.groupable === false) {
      formattedColumn.groupable = false;
    } else {
      if (defaultGroupable === undefined || defaultGroupable === true) {
        // Groupable dle typu sloupce
        formattedColumn.groupable = supportsGrouping(column.type);
      } else {
        formattedColumn.groupable = false;
      }
    }
  }

  return formattedColumn;
};

const convertColumnTypeToAggregatedType = (
  type?: FinalTableColumnType,
  aggregatedType?: FinalTableColumnType,
  currentAggregateType?: TblAggregateType
) => {
  if (aggregatedType) {
    return aggregatedType;
  }

  if (!type) {
    return undefined;
  }

  switch (type) {
    case 'integer':
      if (currentAggregateType === 'avg') {
        return 'decimal';
      } else {
        return 'integer';
      }
    case 'currency':
      if (currentAggregateType === 'avg') {
        return 'currencyDecimal';
      } else {
        return 'currency';
      }
    default:
      return supportsAggregate(type) ? type : undefined;
  }
};

export const getColumnField = (column: FinalTableColumn<any>): string => {
  if (!column.id && !column.accessor) {
    console.error('Sloupec musí mít definován accessor nebo id', column);
    throw new Error('Sloupec musí mít definován accessor nebo id');
  }
  // @ts-ignore
  return column.id || column.accessor;
};

export const getColumnFieldNoCheck = (column: FinalTableColumn<any>) => {
  return column.id || column.accessor;
};

/**
 * Pokud má sloupec pevně definvanou šířku v pixelech, tak tuto hodnotu vrátí, jinak vrátí 0.
 * @param width
 */
export const getColumnPixelWidth = (width?: number | string) => {
  if (typeof width === 'number') {
    return width;
  } else if (typeof width === 'string') {
    if (!width.endsWith('%')) {
      return parseFloat(width);
    }
  }
  return 0;
};

export const computeColumnPixelWidth = (
  column: FinalTableComputedColumn<any>,
  areaWidth: number,
  sumPercentWidth: number,
  columnInfo?: ColumnInfo
) => {
  let width = columnInfo?.width !== undefined ? columnInfo.width : column.width;

  if (typeof width === 'number') {
    return width;
  } else if (typeof width === 'string') {
    if (width.endsWith('%')) {
      return (areaWidth * parseFloat(width)) / sumPercentWidth;
    } else {
      return parseInt(width);
    }
  }

  return undefined;
};

export const supportsAggregate = (type: FinalTableColumnType) => {
  switch (type) {
    case 'date':
    case 'dateTime':
    case 'integer':
    case 'currency':
    case 'currencyDecimal':
    case 'decimal':
      return true;
    default:
      return false;
  }
};

export const arrayMakeSpace = <T>(array: T[], index: number, space: number) => {
  const size = array.length;
  for (let a = size + space - 1; a >= index + space; a--) {
    array[a] = array[a - space];
  }
};

export const getFinalTableKey = (filterKey: AreaKey) => {
  return getAreaKey(filterKey);
};

export const getVisibleColumnsKey = (
  columns: ColumnInfo[],
  allColumns: FinalTableColumn<any>[],
  grouping: DataGroupVO[]
) => {
  const grouped = grouping.map((x) => x.field);

  const idents: string[] = [];

  if (columns.length === 0) {
    allColumns.forEach((c) => {
      let field = getColumnFieldNoCheck(c);
      if (field) {
        idents.push(field);
      }
    });
  } else {
    columns.forEach((c) => {
      if (c.visible) {
        idents.push(c.ident);
      }
    });
  }
  return idents
    .sort((a, b) => {
      const aGroupIndex = grouped.indexOf(a);
      const bGroupIndex = grouped.indexOf(b);
      if (aGroupIndex >= 0 && bGroupIndex >= 0) {
        return aGroupIndex - bGroupIndex;
      }

      // @ts-ignore
      return a.localeCompare(b);
    })
    .join('|');
};

export const getFinalTableDataKey = (kk: string | readonly unknown[]) => {
  let key: string;
  if (Array.isArray(kk)) {
    key = kk.map((x) => JSON.stringify(x)).join('|');
  } else {
    key = `${kk}`;
  }
  return key;
};

export const getColumnTitleAndValue = <D extends object>(
  column: FinalTableComputedColumn<D>,
  rowData: D,
  rowIndex: number,
  columnIndex: number
): { title: string | ReactNode; cell: string | ReactNode; rawValue?: any } => {
  // Tooltip na buňce, může být níže ještě překryt string hodnotou buňky (výsledek volání cell()), ale jen pokud bude shownTitleCanOverride true
  let shownTitle = undefined;
  let shownTitleCanOverride = false;

  let cellValue = getFinalTableColumnCellValue(column, rowData);

  if (column.title) {
    shownTitle = column.title({
      rowIndex: rowIndex,
      columnIndex: columnIndex,
      // @ts-ignore
      row: rowData,
      column,
      value: cellValue,
      formattedValue: column.type
        ? getFormattedValue(column, false, cellValue)
        : undefined,
    });
  } else if (column.columnTitle) {
    shownTitle = column.columnTitle;
  } else if (column.accessor) {
    shownTitleCanOverride = true;
    shownTitle = getFormattedValue(column, false, cellValue) ?? cellValue;

    // Pokud by se stalo, že by hodnotou title byl objekt, tak se title zahodí
    try {
      if (
        shownTitle &&
        typeof shownTitle === 'object' &&
        Object.keys(shownTitle).length > 0
      ) {
        shownTitle = undefined;
      }
    } catch (e) {
      console.log(e);
    }
  } else {
    shownTitleCanOverride = true;
    shownTitle = undefined;
  }

  let cell = column.cell
    ? column.cell({
        rowIndex: rowIndex,
        columnIndex: columnIndex,
        row: rowData,
        column,
        value: cellValue,
        formattedValue: column.type
          ? getFormattedValue(column, false, cellValue)
          : undefined,
      })
    : cellValue;

  if (shownTitleCanOverride && typeof cell === 'string') {
    shownTitle = cell;
  }

  return {
    title: shownTitle,
    cell,
    rawValue: cellValue,
  };
};

export const getFormattedValue = <D extends object>(
  column: FinalTableComputedColumn<D>,
  aggregate: boolean,
  value: any
) => {
  let colType = aggregate ? column.aggregatedType : column.type;
  const timeProps = column.timeProps;
  const numberProps = aggregate
    ? column.aggregatedNumberProps
    : column.numberProps;

  switch (colType) {
    case 'text':
      return value;
    case 'integer':
      return numberProps?.noThousandsSeparator
        ? value
        : value !== null && value !== undefined && formatNumber(value);
    case 'decimal':
      return value && formatNumber(value, numberProps?.fractionDigits || 2);
    case 'date':
      return formatDate(value);
    case 'dateTime':
      return formatDateTime(value, timeProps?.withSeconds);
    case 'currency':
      return value !== undefined && value !== null && formatCurrency(value);
    case 'currencyDecimal':
      return (
        value !== undefined &&
        value !== null &&
        formatCurrency(value, true, numberProps?.fractionDigits || 2)
      );
    case 'boolean':
      return value ? 'Ano' : 'Ne';
    default:
      return undefined;
  }
};

export const getIdOrValueFromRawValue = (cellRawValue?: any) => {
  if (
    cellRawValue !== undefined &&
    cellRawValue !== null &&
    typeof cellRawValue === 'object' &&
    Object.keys(cellRawValue).includes('id')
  ) {
    return cellRawValue.id;
  } else {
    return cellRawValue;
  }
};

export const getColumnByField = <DATA extends object>(
  field: string,
  columns: FinalTableColumn<DATA>[]
) => {
  const result = columns.find((x) => x.id === field || x.accessor === field);
  if (result) {
    return result;
  } else {
    console.error(`Sloupec ${field} nebyl nalezen. Columns: `, columns);
    throw new Error(`Sloupec ${field} nebyl nalezen.`);
  }
};

export const getComputeColumnsWidth = <DATA extends object>(
  areaWidth: number,
  columns: FinalTableComputedColumn<DATA>[],
  columnsInfoMap: { [key: string]: ColumnInfo },
  theme: ThemeProps,
  areaWidthWithoutScrollbar: number,
  defaultColumnWidth: number,
  groupColumnPixelWidth?: number
) => {
  const res: { [key: number]: number } = {};
  // Pixel šířky sloupců ve skupině
  const groupColumnsWidth: number[] = [];

  // Spočtení šířky pevných sloupců
  let fixedWidth = 0;
  let sumPercentWidth = 0;
  columns.forEach((col) => {
    const columnInfo = columnsInfoMap[col.id || col.accessor || ''];

    if (col.group) {
      let groupedColumnWidth =
        groupColumnPixelWidth !== undefined
          ? groupColumnPixelWidth
          : getColumnPixelWidth(
              columnInfo?.width !== undefined ? columnInfo.width : col.width
            );

      if (groupedColumnWidth === 0) {
        col.groupColumns?.forEach((grpCol) => {
          let width =
            columnInfo?.width !== undefined ? columnInfo.width : grpCol.width;
          fixedWidth += getColumnPixelWidth(width);
          if (typeof width === 'string' && width.endsWith('%')) {
            sumPercentWidth += parseFloat(width);
          }
        });
      } else {
        fixedWidth += groupedColumnWidth;
      }
    } else if (col.type === 'action' && col.width === undefined) {
      let size = col.actionSize ?? 1;
      fixedWidth +=
        size * theme.iconButtonWidth +
        (size - 1) * theme.spacers[1] +
        theme.tableCellHorizontalPadding;
    } else {
      let width =
        columnInfo?.width !== undefined ? columnInfo.width : col.width;
      fixedWidth += getColumnPixelWidth(width);
      if (typeof width === 'string' && width.endsWith('%')) {
        sumPercentWidth += parseFloat(width);
      }
    }
  });

  // Kolik zbylo velikosti na sloupce s procentuální šířkou
  let forPercentColsWidth = areaWidthWithoutScrollbar - fixedWidth;
  if (forPercentColsWidth < 0) {
    forPercentColsWidth = areaWidth;
  }

  columns.forEach((col, index) => {
    const columnInfo = columnsInfoMap[col.id || col.accessor || ''];
    let colPixelWidth: number;

    if (col.group) {
      colPixelWidth =
        groupColumnPixelWidth !== undefined
          ? groupColumnPixelWidth
          : computeColumnPixelWidth(
              col,
              forPercentColsWidth,
              sumPercentWidth,
              columnInfo
            ) || 0;
      if (colPixelWidth === 0) {
        col.groupColumns?.forEach((grpCol) => {
          let groupSubColPixelWidth =
            computeColumnPixelWidth(
              grpCol,
              forPercentColsWidth,
              sumPercentWidth,
              columnInfo
            ) || defaultColumnWidth;
          groupColumnsWidth.push(groupSubColPixelWidth);
          colPixelWidth += groupSubColPixelWidth;
        });
      }
    } else if (col.type === 'action' && col.width === undefined) {
      let size = col.actionSize || 1;
      colPixelWidth =
        size * theme.iconButtonWidth +
        (size - 1) * theme.spacers[1] +
        theme.tableCellHorizontalPadding;
    } else {
      colPixelWidth =
        computeColumnPixelWidth(
          col,
          forPercentColsWidth,
          sumPercentWidth,
          columnInfo
        ) || defaultColumnWidth;
    }

    res[index] = colPixelWidth;
  });
  res[columns.length] = theme.scrollbarWidth;

  return { widthsMap: res, groupColumnsWidth };
};

const supportsSorting = (type?: FinalTableColumnType) => {
  if (!type) {
    return false;
  }

  switch (type) {
    case 'text':
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
    case 'date':
    case 'dateTime':
    case 'boolean':
      return true;
    default:
      return false;
  }
};

const supportsGrouping = (type?: FinalTableColumnType) => {
  if (!type) {
    return false;
  }

  switch (type) {
    case 'text':
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
    case 'date':
    case 'boolean':
      return true;
    default:
      return false;
  }
};

export const isColumnSupportsFilter = <DATA extends object>(
  column: FinalTableComputedColumn<DATA>
) => {
  if (column.check) {
    return false;
  }
  if (
    column.type === '_group' ||
    column.type === 'action' ||
    column.type === '_check'
  ) {
    return false;
  }

  // Pokud je filterable uvedeno, tak se chováme dle něj
  if (column.filterable === true) {
    return true;
  } else if (column.filterable === false) {
    return false;
  }

  // Pokud není uvedeno, chováme se dle typu sloupce
  if (column.type === undefined) {
    return false;
  }

  switch (column.type) {
    case 'text':
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
    case 'date':
    case 'boolean':
      // case 'dateTime':
      return true;
    default:
      return false;
  }
};

export const isColumnSupportsFulltext = <DATA extends object>(
  column: FinalTableComputedColumn<DATA>
) => {
  if (column.check) {
    return false;
  }
  if (
    column.type === '_group' ||
    column.type === 'action' ||
    column.type === '_check'
  ) {
    return false;
  }

  if (column.type === undefined && (column.sortable || column.groupable)) {
    return true;
  }

  switch (column.type) {
    case undefined:
      return column.filterable;
    case 'text':
    case 'boolean': // size boolean není pro klasiký fulltext v db, ale hodnoty načítáme stejně jako text, jen tam nikdy nepošleme fulltext string
      return true;
    default:
      return false;
  }
};

export const isColumnSupportsRange = (columnType: FinalTableColumnType) => {
  switch (columnType) {
    case 'integer':
    case 'decimal':
    case 'currency':
    case 'currencyDecimal':
    case 'date':
    case 'dateTime':
      return true;
    default:
      return false;
  }
};
